import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import fileMap from '../fileMap.json';
import { getErrors, clearErrors, ErrorEntry } from '../utils/errorLogger';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = localStorage.getItem('user') || sessionStorage.getItem('user');
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'bugs' | 'code'>('bugs');
  const [errors, setErrors] = useState<ErrorEntry[]>(getErrors());

  const handleLogout = () => {
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    navigate('/admin');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFile(e.target.value);
  };

  const handleClearErrors = () => {
    clearErrors();
    setErrors([]);
  };

  if (!user) {
    navigate('/admin');
    return null;
  }

  const fileContent = selectedFile ? (fileMap as { [key: string]: string })[selectedFile] : '';
  const fileExtension = selectedFile.split('.').pop() || '';

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-4">
      <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-6xl text-center">
        <h2 className="text-3xl font-bold text-blue-300 mb-4">Welcome, {user}!</h2>

        <div className="flex justify-center gap-2 mb-6">
          <button
            onClick={() => setActiveTab('bugs')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors duration-300 ${
              activeTab === 'bugs' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            Bug Reports ({errors.length})
          </button>
          <button
            onClick={() => setActiveTab('code')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors duration-300 ${
              activeTab === 'code' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            Source Code
          </button>
        </div>

        {activeTab === 'bugs' && (
          <div>
            {errors.length === 0 ? (
              <p className="text-gray-400 mb-4">No bugs reported. The website is running smoothly!</p>
            ) : (
              <>
                <div className="text-left w-full overflow-auto mb-4" style={{ maxHeight: '50vh' }}>
                  {errors.map((err, index) => (
                    <div key={index} className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-2">
                      <p className="text-red-400 font-medium break-words">{err.message}</p>
                      <p className="text-gray-500 text-sm mt-1">
                        {err.source} &middot; {new Date(err.time).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setErrors(getErrors())}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300"
                  >
                    Refresh
                  </button>
                  <button
                    onClick={handleClearErrors}
                    className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300"
                  >
                    Clear Bugs
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'code' && (
          <div>
            <p className="text-gray-400 mb-6">Select a file to view its source code.</p>
            <div className="mb-4">
              <select
                onChange={handleFileChange}
                value={selectedFile}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="" disabled>Select a file</option>
                {Object.keys(fileMap).map(filePath => (
                  <option key={filePath} value={filePath}>{filePath}</option>
                ))}
              </select>
            </div>
            {selectedFile && (
              <div className="text-left w-full overflow-auto" style={{ maxHeight: '50vh' }}>
                <h3 className="text-xl font-semibold text-green-300 mb-2">{selectedFile}</h3>
                <SyntaxHighlighter language={fileExtension} style={vscDarkPlus} showLineNumbers>
                  {fileContent}
                </SyntaxHighlighter>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleLogout}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300 mt-4"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default AdminDashboard;
